import React, { useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import BudgetTemplate from './BudgetTemplate';
import { Download, Eye, Loader2 } from 'lucide-react';

const mockBudget = {
  company: {
    name: 'Santiago Móveis Planejados',
    tagline: 'MÓVEIS PLANEJADOS',
    logo: null
  },
  customer: {
    name: 'Karina Maia',
    phone: '(11) 98765-4321',
    email: 'karina.maia@email.com'
  },
  project: {
    date: '17/07/2026',
    validity: '17/08/2026',
    number: 'ORQ-2026-0042'
  },
  products: [
    {
      id: 'p1',
      name: 'Cozinha Planejada Premium',
      category: 'COZINHA',
      description: 'Ambiente completo com armários superiores e inferiores, acabamento em MDF ultra.',
      specifications: [
        'MDF 18mm na cor branco tx',
        'Dobradiças Blum com amortecimento',
        'Corrediças telescópicas invisíveis',
        'Puxadores em alumínio escovado'
      ],
      price: 18500.0,
      image: null
    },
    {
      id: 'p2',
      name: 'Closet Master',
      category: 'QUARTO',
      description: 'Closet sob medida com portas de correr, maleiro e sapateira.',
      specifications: [
        'Portas de correr com perfil em alumínio',
        'Iluminação LED embutida',
        'Maleiro em todo o comprimento',
        'Sapateira com inclinação ergonômica'
      ],
      price: 12300.0,
      image: null
    },
    {
      id: 'p3',
      name: 'Home Office Completo',
      category: 'ESCRITÓRIO',
      description: 'Estação de trabalho com prateleiras e nichos decorativos.',
      specifications: [
        'Mesa com 1.80m de comprimento',
        'Nichos decorativos iluminados',
        'Passa-fios embutido',
        'Gavetas com corrediças invisíveis'
      ],
      price: 6850.0,
      image: null
    }
  ],
  payment: {
    cash: '5% de desconto à vista no PIX ou Dinheiro',
    card: 'Em até 12x sem juros no cartão',
    boleto: 'Entrada + parcelas via boleto',
    installments: '12x de R$ 3.137,50 sem juros'
  },
  totals: {
    subtotal: 37650.0,
    excludedValue: 0,
    discount: 0,
    shipping: 0,
    assembly: 0,
    total: 37650.0
  },
  advantages: [
    'Garantia de 5 anos na estrutura',
    'Projeto 3D incluso',
    'Medição técnica gratuita',
    'Montagem especializada',
    'Suporte pós-venda dedicado'
  ],
  excludedItems: [
    'Elétrica e pontos de luz',
    'Gesso e acabamentos de teto',
    'Granito e pedras em geral',
    'Pintura das paredes'
  ],
  observations: [
    'Prazo de entrega: 35 dias úteis após aprovação',
    'Medição técnica confirmada antes da produção',
    'Garantia conforme contrato de prestação de serviço'
  ]
};

const BudgetPreviewTest = () => {
  const printRef = useRef(null);
  const [generating, setGenerating] = useState(false);

  const handleGeneratePdf = async () => {
    if (!printRef.current) return;
    setGenerating(true);
    try {
      const element = printRef.current;
      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#f6f6f6',
        windowWidth: element.offsetWidth,
        windowHeight: element.offsetHeight
      });
      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const ratio = canvas.width / canvas.height;
      const totalPdfHeight = pdfWidth / ratio;
      let position = 0;
      let heightLeft = totalPdfHeight;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, totalPdfHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - totalPdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, totalPdfHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save('teste-orcamento-premium.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#e5e7eb', padding: '16px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        >
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#243224', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
              <Eye size={24} color="#48984f" />
              Teste de PDF Premium
            </h1>
            <p style={{ fontSize: '13px', color: '#5b665b', margin: '4px 0 0 0' }}>
              Documento A4 em tamanho real (210x297mm). Use scroll horizontal em telas menores.
            </p>
          </div>
          <button
            onClick={handleGeneratePdf}
            disabled={generating}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: generating ? '#9ca3af' : '#48984f',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: generating ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {generating ? (
              <>
                <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                Gerando PDF...
              </>
            ) : (
              <>
                <Download size={18} />
                Gerar PDF de Teste
              </>
            )}
          </button>
        </div>

        <div
          style={{
            background: '#d1d5db',
            borderRadius: '8px',
            padding: '16px',
            overflow: 'auto',
            boxShadow: 'inset 0 0 4px rgba(0,0,0,0.1)',
            maxHeight: 'calc(100vh - 140px)'
          }}
        >
          <div
            style={{
              display: 'inline-block',
              minWidth: '210mm'
            }}
          >
            <div ref={printRef}>
              <BudgetTemplate budget={mockBudget} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetPreviewTest;
